import React, { useState } from 'react';
import { ChevronRightIcon, MailIcon, InboxIcon, SendIcon, BookmarkIcon, ClockIcon } from 'lucide-react';
import { InboundEmailConfig } from './InboundEmailConfig';
import { EmailRules } from './EmailRules';
import { OutboundEmailConfig } from './OutboundEmailConfig';
import { EmailAuditLog } from './EmailAuditLog';
export type ScopeLevel = 'Tenant' | 'Entity' | 'Site';
export type InboundEmailConfig = {
  id: string;
  scopeLevel: ScopeLevel;
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
  emailAddress: string;
  imapHost: string;
  imapPort: number;
  useSSL: boolean;
  username: string;
  password: string;
  pollingFrequencyMinutes: number;
  defaultDocumentType: string;
  defaultTags: string[];
  acceptEmailBodyAsAttachment: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  rules: EmailRule[];
};
export type EmailRule = {
  id: string;
  inboundEmailConfigId: string;
  senderMatch: string;
  subjectContains: string;
  attachmentTypesAllowed: string[];
  applyTags: string[];
  reclassifyAs?: string;
  autoForwardTo?: string;
  ignoreIfNoAttachment: boolean;
  priority: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};
export type OutboundEmailConfig = {
  id: string;
  scopeLevel: ScopeLevel;
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
  sendingMethod: 'SMTP' | 'Microsoft 365' | 'Gmail' | 'API';
  smtpHost?: string;
  smtpPort?: number;
  useTLS: boolean;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  replyTo: string;
  emailFooter?: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};
export type EmailAuditEntry = {
  id: string;
  direction: 'Inbound' | 'Outbound';
  eventType: 'Success' | 'Error' | 'Warning' | 'Info';
  emailAddress: string;
  subject: string;
  message: string;
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
  timestamp: string;
  attachmentsCount?: number;
  documentIds?: string[];
};
export function EmailConfiguration({
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState<'inbound' | 'rules' | 'outbound' | 'audit'>('inbound');
  // Mock data for inbound email configurations
  const [inboundConfigs, setInboundConfigs] = useState<InboundEmailConfig[]>([{
    id: 'inbound-001',
    scopeLevel: 'Tenant',
    emailAddress: 'ap@example.com',
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    useSSL: true,
    username: 'ap@example.com',
    password: '********',
    pollingFrequencyMinutes: 15,
    defaultDocumentType: 'Invoice',
    defaultTags: ['AP', 'Email'],
    acceptEmailBodyAsAttachment: true,
    isActive: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z',
    rules: [{
      id: 'rule-001',
      inboundEmailConfigId: 'inbound-001',
      senderMatch: '*@acme.com',
      subjectContains: 'Invoice',
      attachmentTypesAllowed: ['pdf', 'tiff', 'jpg'],
      applyTags: ['Acme Corp', 'Priority'],
      reclassifyAs: 'Invoice',
      ignoreIfNoAttachment: true,
      priority: 1,
      isActive: true,
      createdAt: '2023-01-01T00:00:00Z',
      updatedAt: '2023-01-01T00:00:00Z'
    }]
  }, {
    id: 'inbound-002',
    scopeLevel: 'Entity',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    emailAddress: 'invoices@example.com',
    imapHost: 'outlook.office365.com',
    imapPort: 993,
    useSSL: true,
    username: 'invoices@example.com',
    password: '********',
    pollingFrequencyMinutes: 30,
    defaultDocumentType: 'Invoice',
    defaultTags: ['Email Import'],
    acceptEmailBodyAsAttachment: false,
    isActive: true,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z',
    rules: []
  }]);
  // Mock data for outbound email configurations
  const [outboundConfigs, setOutboundConfigs] = useState<OutboundEmailConfig[]>([{
    id: 'outbound-001',
    scopeLevel: 'Tenant',
    sendingMethod: 'SMTP',
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    useTLS: true,
    username: 'noreply@example.com',
    password: '********',
    fromEmail: 'noreply@example.com',
    fromName: 'RapidP2P System',
    replyTo: 'support@example.com',
    emailFooter: '<p>This is an automated message from the RapidP2P system.</p>',
    isDefault: true,
    isActive: true,
    createdAt: '2023-01-01T00:00:00Z',
    updatedAt: '2023-01-01T00:00:00Z'
  }, {
    id: 'outbound-002',
    scopeLevel: 'Entity',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    sendingMethod: 'Microsoft 365',
    useTLS: true,
    username: 'notifications@globaltechnologies.com',
    password: '********',
    fromEmail: 'notifications@globaltechnologies.com',
    fromName: 'Global Technologies',
    replyTo: 'support@globaltechnologies.com',
    emailFooter: '<p>Global Technologies Inc. - All rights reserved.</p>',
    isDefault: false,
    isActive: true,
    createdAt: '2023-02-01T00:00:00Z',
    updatedAt: '2023-02-01T00:00:00Z'
  }]);
  // Mock data for email audit log
  const [auditEntries, setAuditEntries] = useState<EmailAuditEntry[]>([{
    id: 'audit-001',
    direction: 'Inbound',
    eventType: 'Success',
    emailAddress: 'vendor@acme.com',
    subject: 'Invoice #12345',
    message: 'Email processed successfully, 1 attachment extracted',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    timestamp: '2023-05-15T10:30:00Z',
    attachmentsCount: 1,
    documentIds: ['doc-12345']
  }, {
    id: 'audit-002',
    direction: 'Outbound',
    eventType: 'Error',
    emailAddress: 'john.doe@example.com',
    subject: 'Approval Required: Invoice #54321',
    message: 'Failed to deliver: Recipient mailbox full',
    entityId: 'ent-001',
    entityName: 'Global Technologies Inc.',
    timestamp: '2023-05-14T15:45:00Z'
  }]);
  // Add handler for breadcrumb navigation
  const handleBreadcrumbClick = (level: string) => {
    if (level === 'settings') {
      // In a real app, this would navigate to settings page
      setActiveTab('inbound');
    }
  };
  // Handle tab switching
  const renderTabContent = () => {
    switch (activeTab) {
      case 'inbound':
        return <InboundEmailConfig inboundConfigs={inboundConfigs} setInboundConfigs={setInboundConfigs} locale={locale} data-id="element-16007" />;
      case 'rules':
        return <EmailRules inboundConfigs={inboundConfigs} setInboundConfigs={setInboundConfigs} locale={locale} data-id="element-16008" />;
      case 'outbound':
        return <OutboundEmailConfig outboundConfigs={outboundConfigs} setOutboundConfigs={setOutboundConfigs} locale={locale} data-id="element-16009" />;
      case 'audit':
        return <EmailAuditLog auditEntries={auditEntries} locale={locale} data-id="element-16010" />;
      default:
        return <InboundEmailConfig inboundConfigs={inboundConfigs} setInboundConfigs={setInboundConfigs} locale={locale} data-id="element-16011" />;
    }
  };
  return <div data-id="element-16012">
      <div className="mb-8" data-id="element-16013">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-16014">
          Email Configuration
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16015">
          Manage inbound email polling for document ingestion and outbound email
          sending settings
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6" data-id="element-16016">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-16017">
          <button className={`${activeTab === 'inbound' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} onClick={() => setActiveTab('inbound')} data-id="element-16018">
            <InboxIcon className="h-4 w-4 mr-2" data-id="element-16019" />
            Inbound Email
          </button>
          <button className={`${activeTab === 'rules' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} onClick={() => setActiveTab('rules')} data-id="element-16020">
            <BookmarkIcon className="h-4 w-4 mr-2" data-id="element-16021" />
            Rules
          </button>
          <button className={`${activeTab === 'outbound' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} onClick={() => setActiveTab('outbound')} data-id="element-16022">
            <SendIcon className="h-4 w-4 mr-2" data-id="element-16023" />
            Outbound Email
          </button>
          <button className={`${activeTab === 'audit' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} onClick={() => setActiveTab('audit')} data-id="element-16024">
            <ClockIcon className="h-4 w-4 mr-2" data-id="element-16025" />
            Audit Log
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      {renderTabContent()}
    </div>;
}